import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { CreateSpaceAnalyticsEvents, ExtendedMapTemplate } from '@zep/types';
import { Button } from '@zep/ui';
import { cn } from '@zep/utils';
import { createSpaceFormDefaults, CreateSpaceSchema, CreateSpaceSchemaType, SpaceSettingsForm, TemplateCard, useCreateSpace } from '@zep/web-components';
import { useCheckContentScrollEffect } from '../hooks';
interface CreateSpacePanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTemplate: ExtendedMapTemplate | null;
}
export const CreateSpacePanel = ({
  isOpen,
  selectedTemplate
}: CreateSpacePanelProps) => {
  const {
    t
  } = useTranslation(['common', 'home']);
  const router = useRouter();
  const {
    contentRef,
    hasScroll
  } = useCheckContentScrollEffect(isOpen);
  const isCreatingRef = useRef(false);
  const methods = useForm<CreateSpaceSchemaType>({
    resolver: zodResolver(CreateSpaceSchema),
    defaultValues: createSpaceFormDefaults
  });

  // 템플릿 변경 시 폼 초기화 및 ref 초기화
  useEffect(() => {
    if (selectedTemplate && isOpen) {
      methods.reset(createSpaceFormDefaults);
      isCreatingRef.current = false;
    }
  }, [selectedTemplate, methods, isOpen]);
  const {
    watch,
    handleSubmit,
    formState: {
      errors
    }
  } = methods;
  const name = watch('name');
  const enablePassword = watch('enablePassword');
  const password = watch('password');
  const {
    createSpace,
    isLoading
  } = useCreateSpace({
    analyticsEvent: CreateSpaceAnalyticsEvents.home,
    place: 'home',
    onSuccess: (spaceHashId, entryMapHashId) => {
      isCreatingRef.current = true;
      if (selectedTemplate?.isEmpty) {
        router.push(`/edit/${spaceHashId}/${entryMapHashId}`, undefined, {
          locale: false
        });
      } else {
        router.push(`/play/${entryMapHashId}`);
      }
    }
  });
  const handleCreateSpace = async (data: CreateSpaceSchemaType) => {
    if (!selectedTemplate || isCreatingRef.current) return;
    await createSpace(data, {
      hashId: selectedTemplate.hashId,
      name: selectedTemplate.name,
      isPurchased: selectedTemplate.isPurchased
    });
  };
  const isValid = Object.keys(errors).length === 0 && name?.trim().length > 0 && (!enablePassword || enablePassword && password?.trim().length > 0);
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="CreateSpacePanel" data-sentry-source-file="CreateSpacePanel.tsx">
      {isOpen && <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleCreateSpace)}>
            <motion.div className="fixed top-[64px] flex h-[calc(100vh-64px)] supports-[height:100dvh]:h-[calc(100dvh-64px)]" style={{
          right: '0',
          width: 'calc(404px + max(0px, (100vw - 1142px) / 2))'
        }} initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }}>
              <div className="flex h-full w-[404px] flex-col bg-white">
                <div className="flex h-full flex-col">
                  <div ref={contentRef} className="flex min-h-0 flex-1 flex-col gap-[26px] overflow-y-auto py-6 pl-[40px] pr-[10px] pt-[40px]">
                    {selectedTemplate && <div className="pointer-events-none">
                        <span className="text-subtitle font-semibold text-gray-900">
                          {t('common:createSpace.selectedTemplate')}
                        </span>
                        <TemplateCard template={selectedTemplate} variant="panel" />
                      </div>}
                    <SpaceSettingsForm disabled={isLoading} />
                  </div>
                  <div className={cn('flex flex-col text-center gap-[12px] pl-[40px] pb-[40px] pt-[16px] pr-[10px] transition-shadow flex-shrink-0', hasScroll && 'shadow-[0_-6px_4px_-4px_rgba(42,39,65,0.12)]')}>
                    <span className="text-body-2 text-gray-600">
                      {t('common:createSpaceModal.loadingText')}
                    </span>
                    <Button type="submit" variant="primary" size="lg" isFullWidth isLoading={isLoading || isCreatingRef.current} disabled={!isValid || isLoading || isCreatingRef.current}>
                      {t('home:space_me_create_space_button')}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="h-full flex-1 bg-white" />
            </motion.div>
          </form>
        </FormProvider>}
    </AnimatePresence>;
};